import { useEffect, useRef, useState } from "react";
import * as turf from "@turf/turf";
import { useSearchParams } from "react-router-dom";
import { MapType, SettlementSelectedType } from "../../../types/dataTypes";
import { getGeoJsonByName, getSettlementById } from "../../../services/getGeojsonData.service";
import { getAllSurveySettlements, SettlementDto } from "../../../services/surveySettlement.service";
import { LAYER_IDS, LAYER_ORDER_ARRAY } from "../../../utils/mapConsts";
import { mapCorrectLayerOrder, mapRemoveLayerSource } from "../../../utils/mapDataHandler";
import { searchParamsObj } from "../../../utils/dataHandler";
import { COLORS, COLORS_BASE, SLIDER_OPTIONS } from "../../../utils/consts";
import SetttlementPopup from "../SettlementPopup";

type SelectedLayersProps = {
    mapLoaded: boolean;
    map: MapType;
    dynamic_radius: boolean;
    setNumberSelectedSettlements: Function;
}

function SelectedLayers({ map, mapLoaded, dynamic_radius, setNumberSelectedSettlements }: SelectedLayersProps) {
    const mapCurrent = map.current;
    const settlementsTouchLayerId = `${LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS}_touch`;
    const surveyedTouchLayerId = `${LAYER_IDS.SURVEYED_SETTLEMENTS}_touch`;
    const [searchParams] = useSearchParams();
    const sp = searchParamsObj(searchParams);
    const [detailsOpen, setDetailsOpen] = useState<Boolean>(false);
    const [setlectedSettlement, setSelectedSettlement] = useState<SettlementSelectedType | undefined>(undefined);
    const eventHandlersBound = useRef(false);
    const lastTouchInteractionRef = useRef(0);
    const surveySettlementsRef = useRef<SettlementDto[]>([]);

    const openFeaturedPopup = (id: number, coordinates?: number[]) => {
        const profile = surveySettlementsRef.current.find((s) => s.id === id);
        if (!profile) return;

        const fallbackCoordinates = [
            profile.settlement_identity.coordinates.longitude,
            profile.settlement_identity.coordinates.latitude,
        ];
        const popupCoordinates = coordinates || fallbackCoordinates;

        setSelectedSettlement({
            id: profile.id,
            name: profile.settlement_identity.village_name,
            building_c: profile.basic_scale.total_households ?? 0,
            grid_dist: profile.basic_scale.distance_to_grid_km ?? 0,
            area_km2: 0,
            grid_d_km: profile.basic_scale.distance_to_grid_km ?? 0,
            build_d_pe: 0,
            coordinates: popupCoordinates,
            bbox: turf.bbox(turf.point(fallbackCoordinates)) as [number, number, number, number],
            featuredProfile: profile,
        });
        setDetailsOpen(true);
    };
    


    const initialSliderVals = SLIDER_OPTIONS.map((option) => ({
        ...option,
        minValInit: sp[`${option.param}_min`] ? Number(sp[`${option.param}_min`]) : option.minValInit,
        maxValInit: sp[`${option.param}_max`] ? Number(sp[`${option.param}_max`]) : option.maxValInit
    }));

    useEffect(() => {
        if (!mapLoaded || !mapCurrent) return;

        // Fetch and add the GOVS data layer
        getGeoJsonByName(LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS).then((res) => {
            if (!res || res.statusCode === 500 || !Array.isArray(res.features)) {
                console.error("Settlement data not available or invalid response format.");
                return;
            }
            const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
            
            // Remove existing layers and sources before adding new data
            mapRemoveLayerSource(mapCurrent, [LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS, settlementsTouchLayerId, LAYER_IDS.SURVEYED_SETTLEMENTS_HALO, LAYER_IDS.SURVEYED_SETTLEMENTS, surveyedTouchLayerId]);

            // Filter settlements based on slider values
            const filteredFeatures = res.features.filter((feature: any) => {
                return initialSliderVals.every((slider) => {
                    const value = feature.properties[slider.param];
                    return value >= slider.minValInit && value <= slider.maxValInit;
                });
            });

            const filteredData = { ...res, features: filteredFeatures };

            setNumberSelectedSettlements(filteredFeatures.length);

            // Add the filtered settlements layer as source
            mapCurrent.addSource(LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS, {
                type: "geojson",
                data: filteredData,
                generateId: true,
                attribution: `Boundaries: <a href="https://data.humdata.org/dataset/geoboundaries-admin-boundaries-for-algeria">geoboundaries</a> <a href="https://data.humdata.org/faqs/licenses">CC BY-IGO</a>`,
            });

            if (!dynamic_radius) {
                mapCurrent.addLayer({
                    id: LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS,
                    type: "circle",
                    source: LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS,
                    layout: { visibility: "visible" },
                    paint: {
                        "circle-radius": isMobile ? 7 : 4,
                        "circle-color": COLORS.MAP.SETTLEMENTS,
                        "circle-stroke-color": 'white',
                        "circle-stroke-width": 2,
                    },
                });
            }

            mapCurrent.addLayer({
                id: settlementsTouchLayerId,
                type: "circle",
                source: LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS,
                layout: { visibility: "visible" },
                paint: {
                    "circle-radius": isMobile ? 18 : 12,
                    "circle-color": "#000000",
                    "circle-opacity": 0,
                    "circle-stroke-width": 0,
                },
            });

        // Fetch survey settlements and add surveyed settlements layer
        getAllSurveySettlements().then((surveyData) => {
            if (!Array.isArray(surveyData)) return;
            surveySettlementsRef.current = surveyData;

            const surveyGeojson = {
                type: 'FeatureCollection',
                features: surveyData.map((dto) => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [
                            dto.settlement_identity.coordinates.longitude,
                            dto.settlement_identity.coordinates.latitude,
                        ],
                    },
                    properties: {
                        id: dto.id,
                        name: dto.settlement_identity.village_name,
                        district: dto.settlement_identity.district,
                    },
                })),
            };

            if (mapCurrent.getSource(LAYER_IDS.SURVEYED_SETTLEMENTS)) {
                (mapCurrent.getSource(LAYER_IDS.SURVEYED_SETTLEMENTS) as any).setData(surveyGeojson);
            } else {
                mapCurrent.addSource(LAYER_IDS.SURVEYED_SETTLEMENTS, {
                    type: 'geojson',
                    data: surveyGeojson as any,
                    generateId: true,
                });

                // Outer glow / halo
                mapCurrent.addLayer({
                    id: LAYER_IDS.SURVEYED_SETTLEMENTS_HALO,
                    type: 'circle',
                    source: LAYER_IDS.SURVEYED_SETTLEMENTS,
                    layout: { visibility: 'visible' },
                    paint: {
                        'circle-radius': 18,
                        'circle-color': '#F59E0B',
                        'circle-opacity': 0.25,
                        'circle-stroke-width': 0,
                    },
                });

                // Inner dot
                mapCurrent.addLayer({
                    id: LAYER_IDS.SURVEYED_SETTLEMENTS,
                    type: 'circle',
                    source: LAYER_IDS.SURVEYED_SETTLEMENTS,
                    layout: { visibility: 'visible' },
                    paint: {
                        'circle-radius': 9,
                        'circle-color': '#F59E0B',
                        'circle-stroke-color': 'white',
                        'circle-stroke-width': 2,
                    },
                });

                mapCurrent.addLayer({
                    id: surveyedTouchLayerId,
                    type: 'circle',
                    source: LAYER_IDS.SURVEYED_SETTLEMENTS,
                    layout: { visibility: 'visible' },
                    paint: {
                        'circle-radius': isMobile ? 22 : 16,
                        'circle-color': '#000000',
                        'circle-opacity': 0,
                        'circle-stroke-width': 0,
                    },
                });
            }

            mapCorrectLayerOrder(mapCurrent, LAYER_ORDER_ARRAY);

            if (typeof sp.featured_settlement === 'string' && sp.featured_settlement.length > 0) {
                const id = parseInt(sp.featured_settlement, 10);
                const featured = surveyData.find((s) => s.id === id);
                if (featured) {
                    mapCurrent.flyTo({
                        center: [
                            featured.settlement_identity.coordinates.longitude,
                            featured.settlement_identity.coordinates.latitude,
                        ],
                        zoom: 10,
                    });
                    openFeaturedPopup(id);
                }
            }
        });

            if (dynamic_radius) {
                // Add the SETTLEMENTS layer and make it visible always
                mapCurrent.addLayer({
                    id: LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS,
                    type: "circle",
                    source: LAYER_IDS.SETTLEMENTS_ALL_CENTROIDS,
                    layout: { visibility: "visible" },
                    paint: {
                        "circle-radius": [
                            'step',
                            ['get', 'building_c'],
                            3, // Default radius
                            10, 5, // < 10
                            50, 7, // < 50
                            100, 9, // < 100
                            500, 11, // < 500
                            1000, 13, // < 1000
                            10000, 20 // >= 1000
                        ],
                        "circle-color": [
                            'step',
                            ['get', 'building_c'],
                            '#00FF00', // Green for < 10
                            10, '#7FFF00', // Yellow-green for < 50
                            50, '#FFFF00', // Yellow for < 100
                            100, '#FFBF00', // Orange for < 500
                            500, '#FF8000', // Dark orange for < 1000
                            1000, '#FF0000' // Red for >= 1000
                        ],
                        "circle-opacity": 0.5,
                    },
                });
            }

            mapCorrectLayerOrder(mapCurrent, LAYER_ORDER_ARRAY);

            if (!eventHandlersBound.current) {
                const handleSettlementFeature = (feature: any) => {
                    const settlementId = feature.properties.fid;

                    setSelectedSettlement(undefined);

                    // Fetch the settlement geometry from the API endpoint
                    getSettlementById(settlementId)
                        .then(data => {
                            if (!data || data.statusCode === 500 || !Array.isArray(data.features) || !data.features[0]) {
                                console.error("Settlement geometry not available or invalid response format.");
                                return;
                            }

                            mapRemoveLayerSource(mapCurrent, [LAYER_IDS.SETTLEMENT_SELECTED]);

                            // Add the settlement geometry to the map
                            mapCurrent.addSource(LAYER_IDS.SETTLEMENT_SELECTED, {
                                type: "geojson",
                                data: data,
                                generateId: true,
                                attribution: `Boundaries: <a href="https://data.humdata.org/dataset/geoboundaries-admin-boundaries-for-algeria">geoboundaries</a> <a href="https://data.humdata.org/faqs/licenses">CC BY-IGO</a>`,
                            });

                            mapCurrent.addLayer({
                                id: LAYER_IDS.SETTLEMENT_SELECTED,
                                type: "line",
                                source: LAYER_IDS.SETTLEMENT_SELECTED,
                                layout: { visibility: "visible" },
                                paint: {
                                    "line-color": COLORS_BASE.BRAND.MAIN,
                                    "line-width": 3,
                                  },
                            });

                            setSelectedSettlement(
                                {
                                    id: data.features[0].properties.fid,
                                    name: data.features[0].properties.name,
                                    grid_d_km: data.features[0].properties.grid_d_km,
                                    coordinates: feature.geometry.coordinates,
                                    building_c: data.features[0].properties.building_c,
                                    grid_dist: data.features[0].properties.grid_dist,
                                    area_km2: data.features[0].properties.area_km2,
                                    build_d_pe: data.features[0].properties.build_d_pe,
                                    bbox: turf.bbox(data.features[0]) as [number, number, number, number]
                                });

                            setDetailsOpen(true);

                            mapCorrectLayerOrder(mapCurrent, LAYER_ORDER_ARRAY);
                        })
                        .catch(error => {
                            console.error("Error fetching settlement geometry: ", error);
                        });
                };

                const handleSurveyedSettlementFeature = (feature: any) => {
                    const id = feature.properties.id as number;
                    mapRemoveLayerSource(mapCurrent, [LAYER_IDS.SETTLEMENT_SELECTED]);
                    openFeaturedPopup(id, feature.geometry.coordinates);
                };

                const handleSettlementSelect = (e: any) => {
                    if (Date.now() - lastTouchInteractionRef.current < 500) return;
                    if (e.features && e.features.length > 0) {
                        handleSettlementFeature(e.features[0] as any);
                    }
                };

                const handleSurveyedSettlementSelect = (e: any) => {
                    if (Date.now() - lastTouchInteractionRef.current < 500) return;
                    if (e.features && e.features.length > 0) {
                        handleSurveyedSettlementFeature(e.features[0] as any);
                    }
                };

                mapCurrent.on('click', settlementsTouchLayerId, handleSettlementSelect);
                mapCurrent.on('click', surveyedTouchLayerId, handleSurveyedSettlementSelect);

                mapCurrent.on('touchend', (e: any) => {
                    if (!e.point) return;

                    const touchFeatures = mapCurrent.queryRenderedFeatures(e.point, {
                        layers: [surveyedTouchLayerId, settlementsTouchLayerId],
                    });

                    if (!touchFeatures || touchFeatures.length === 0) return;

                    lastTouchInteractionRef.current = Date.now();
                    const topFeature = touchFeatures[0] as any;

                    if (topFeature.layer?.id === surveyedTouchLayerId) {
                        handleSurveyedSettlementFeature(topFeature);
                        return;
                    }

                    if (topFeature.layer?.id === settlementsTouchLayerId) {
                        handleSettlementFeature(topFeature);
                    }
                });

                // Change cursor to pointer on hover
                mapCurrent.on('mouseenter', settlementsTouchLayerId, () => {
                    mapCurrent.getCanvas().style.cursor = 'pointer';
                });

                mapCurrent.on('mouseleave', settlementsTouchLayerId, () => {
                    mapCurrent.getCanvas().style.cursor = '';
                });

                mapCurrent.on('mouseenter', surveyedTouchLayerId, () => {
                    mapCurrent.getCanvas().style.cursor = 'pointer';
                });

                mapCurrent.on('mouseleave', surveyedTouchLayerId, () => {
                    mapCurrent.getCanvas().style.cursor = '';
                });

                eventHandlersBound.current = true;
            }

        });
    }, [mapLoaded, searchParams]);

    return (
        <>
            {detailsOpen && setlectedSettlement && (
            <SetttlementPopup
                selectedSettlement={setlectedSettlement}
                onClose={() => setDetailsOpen(false)}
                map={map}
            />
            )}
        </>
        );
}

export default SelectedLayers;